import PageHeading from '../components/PageHeading';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle, Copy, Check, ChevronDown, Loader2, ShieldCheck, LockKeyhole, Eye, EyeOff, ArrowRight, Search } from 'lucide-react';
import { loginByToken } from '../store/userSlice';
import { getOrderProductInfo, getOrderProductInfoWithPassword } from '../api/payment';

const FAQ_ITEMS = [
  {
    id: 'how-to-recharge',
    question: '如何自助充值升级？',
    answer: (
      <div className="space-y-2.5">
        {[
          '复制卡密 → 点击激活地址 → 登录 ChatGPT 账号',
          '粘贴卡密并确认 → 刷新页面，看到 Plus 即成功',
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-brand-soft text-brand text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
            <span className="text-xs text-ink leading-relaxed">{step}</span>
          </div>
        ))}
        <a
          href="/upgrade-guide"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between mt-3 px-4 py-3 bg-brand hover:bg-brand-hover rounded-xl transition-colors group"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">📖</span>
            <div>
              <p className="text-white text-xs font-bold leading-tight">不知道怎么操作？</p>
              <p className="text-orange-100 text-[10px] leading-tight">查看图文详细教程，一步一步跟着做</p>
            </div>
          </div>
          <span className="text-white font-bold text-sm group-hover:translate-x-0.5 transition-transform">→</span>
        </a>
      </div>
    ),
  },
  {
    id: 'not-showing-plus',
    question: '充值后没显示 Plus 升级怎么办？',
    answer: (
      <div className="space-y-3">
        <p className="text-xs text-muted leading-relaxed">
          充值显示成功但 Plus 未到账，<strong className="text-ink">这是正常现象，不是出错了</strong>，请放心。
        </p>
        <div className="bg-canvas border border-line rounded-lg px-3 py-2.5 space-y-1.5">
          <p className="text-xs font-semibold text-ink">为什么会有延迟？</p>
          <p className="text-xs text-muted leading-relaxed">
            OpenAI 在全球部署了多个数据中心。你购买 Plus 时，订单数据写入的是其中一个节点；而你登录 ChatGPT 时，连接的可能是另一个地区的节点。OpenAI 内部各节点之间的数据同步需要一定时间，这就导致部分账号会有短暂延迟——<strong className="text-ink">有的账号秒到，有的需要等一会</strong>，都是正常的。
          </p>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-ink">先做这两步：</p>
          {[
            '刷新 ChatGPT 页面',
            '退出账号，重新登录',
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-brand-soft text-brand text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <span className="text-xs text-ink leading-relaxed">{step}</span>
            </div>
          ))}
        </div>
        <div className="bg-brand-soft rounded-lg px-3 py-2 space-y-1">
          <p className="text-xs text-brand font-semibold">生效时间参考</p>
          <p className="text-xs text-muted leading-relaxed">· <strong className="text-ink">80%</strong> 的用户在 <strong className="text-ink">10 分钟内</strong>生效</p>
          <p className="text-xs text-muted leading-relaxed">· <strong className="text-ink">20%</strong> 的用户延迟到 <strong className="text-ink">30 分钟左右</strong>生效</p>
          <p className="text-xs text-muted leading-relaxed">目前暂无激活成功后延迟超过 1 小时的情况</p>
        </div>
        <div className="border border-brand-line rounded-lg px-3 py-2">
          <p className="text-xs text-ink leading-relaxed">
            如果充值成功后 <strong className="text-brand">超过 2 小时</strong>仍未升级为 Plus，我们提供完善售后保障——<strong className="text-brand">充值不成功，100% 退款</strong>。请联系右下角客服气泡并提供订单号。
          </p>
        </div>
      </div>
    ),
  },
];

const hasValidCardKey = (cardKey) => {
  if (cardKey === null || cardKey === undefined) {
    return false;
  }

  const normalizedCardKey = String(cardKey).trim();
  return normalizedCardKey !== '' && normalizedCardKey !== '暂无库存';
};

const QUERY_ERROR_MESSAGES = {
  INVALID_QUERY_PASSWORD: '查询密码错误，请重新输入。',
  ORDER_NOT_FOUND: '未找到该账户的已支付订单。',
  INVALID_REQUEST: '请检查账户、订单号和查询密码是否填写正确。',
  QUERY_PASSWORD_NOT_SET: '此订单未设置查询密码，正在重新查询。',
  ORDER_QUERY_LOCKED: '此订单查询错误次数过多，请联系客服进行解锁。',
};

const getQueryError = (error) => {
  if (typeof error?.code === 'string' && error.code.startsWith('AUTH_')) {
    return { code: error.code, message: error.message, isLocked: false };
  }
  const body = error?.response?.data || error;
  const status = error?.response?.status || body?.status;
  const code = body?.code || body?.data?.code || body?.error?.code || body?.error;
  const isLocked = status === 423 || code === 'ORDER_QUERY_LOCKED'
    || body?.query_locked === true || body?.data?.query_locked === true;
  if (isLocked) return { code: 'ORDER_QUERY_LOCKED', message: QUERY_ERROR_MESSAGES.ORDER_QUERY_LOCKED, isLocked: true };

  let message = QUERY_ERROR_MESSAGES[code];
  const remainingAttempts = body?.remaining_attempts ?? body?.data?.remaining_attempts;
  if ((code === 'INVALID_QUERY_PASSWORD' || status === 403)
    && Number.isInteger(remainingAttempts) && remainingAttempts >= 0) {
    message = `查询密码错误，还剩 ${remainingAttempts} 次机会。`;
  }
  if (status >= 500) message = '查询失败，请稍后重试。';
  if (!message) {
    message = status === 403 ? QUERY_ERROR_MESSAGES.INVALID_QUERY_PASSWORD
      : status === 404 ? QUERY_ERROR_MESSAGES.ORDER_NOT_FOUND
        : status === 400 ? QUERY_ERROR_MESSAGES.INVALID_REQUEST
          : '查询失败，请稍后重试。';
  }
  return { code, message, isLocked: false };
};

const readOrderData = (response) => {
  if (!response?.data || typeof response.data !== 'object' || Array.isArray(response.data)
    || getQueryError(response).isLocked || response.error || response.data.error || QUERY_ERROR_MESSAGES[response.code]
    || QUERY_ERROR_MESSAGES[response.data.code]) {
    throw response;
  }
  const data = { ...response.data };
  delete data.query_password;
  if (data.requires_query_password === true) {
    ['account_name', 'account_password', 'email_password', 'accessToken', 'baseurl', 'card_key'].forEach((field) => {
      delete data[field];
    });
  }
  return data;
};

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const [orderResult, setOrderResult] = useState(null);
  const [isQuerying, setIsQuerying] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [queryError, setQueryError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [refreshIndex, setRefreshIndex] = useState(0);
  const requestVersion = useRef(0);
  const passwordRequestPending = useRef(false);
  const queryLockRef = useRef(false);
  const passwordInputRef = useRef(null);
  const [queryPassword, setQueryPassword] = useState('');
  const [showQueryPassword, setShowQueryPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const orderId = searchParams.get('orderId');
  const userToken = searchParams.get('userToken');
  // 在 effect 清理之前，也不展示上一个订单或账户的查询结果。
  // The token-login request updates Redux asynchronously. Read the persisted
  // uid first so the first order response is not discarded during that update.
  const currentUserUid = localStorage.getItem('uid') || user?.uid;
  const productInfo = orderResult?.orderId === orderId
    && orderResult?.userToken === userToken
    && orderResult?.userUid === currentUserUid
    ? orderResult?.data
    : null;
  const requiresQueryPassword = productInfo?.requires_query_password === true;
  const isQueryLocked = productInfo?.query_locked === true;
  const canSelfActivate = !requiresQueryPassword && hasValidCardKey(productInfo?.card_key);
  const canShowActivation = !requiresQueryPassword && (canSelfActivate || Boolean(productInfo?.baseurl));
  const isManualUpgrade = Boolean(productInfo) && !requiresQueryPassword && !canShowActivation && !productInfo.accessToken;

  let orderDescription = '正在查询订单信息，请稍候。';
  if (isQueryLocked) {
    orderDescription = '此订单的查询已被锁定，账号信息、卡密和充值地址已隐藏。';
  } else if (requiresQueryPassword) {
    orderDescription = '验证通过后，才能查看此单的账号信息、卡密和充值地址。';
  } else if (canSelfActivate) {
    orderDescription = '卡密和激活地址已自动发放，按下方步骤自助完成激活即可。如自助激活失败，请联系客服进行人工兜底处理。';
  } else if (isManualUpgrade) {
    orderDescription = '感谢你的信任。本订单为人工升级，请将订单号发送给客服，由客服为你手动开通。';
  } else if (notLoggedIn || queryError || !orderId) {
    orderDescription = '暂时无法查询订单信息，请查看下方提示。';
  } else if (productInfo) {
    orderDescription = '订单信息已获取，请妥善保存您的账号与充值信息。';
  }

  useEffect(() => {
    let cancelled = false;
    const version = ++requestVersion.current;
    const isCurrent = () => !cancelled && requestVersion.current === version;
    passwordRequestPending.current = false;
    queryLockRef.current = false;
    setIsQuerying(true);
    setIsVerifying(false);
    setOrderResult(null);
    setQueryPassword('');
    setShowQueryPassword(false);
    setNotLoggedIn(false);
    setQueryError('');
    setPasswordError('');
    setCopied(false);
    setCopiedKey(false);
    setCopiedField(null);
    setOpenFaq(null);

    const init = async () => {
      let loggedInUser = user;
      if (userToken) {
        try {
          loggedInUser = await dispatch(loginByToken(userToken)).unwrap();
        } catch (error) {
          console.error('自动登录失败:', error);
        }
      }

      if (!isCurrent()) return;

      // uid 统一从本地取，loginByToken 成功后会写入 localStorage
      const uid = localStorage.getItem('uid') || loggedInUser?.uid || user?.uid;
      if (!uid) {
        setNotLoggedIn(true);
        return;
      }

      if (!orderId) {
        setQueryError(QUERY_ERROR_MESSAGES.INVALID_REQUEST);
      } else {
        try {
          const response = await getOrderProductInfo({ orderId: String(orderId), uid: String(uid) });
          if (!isCurrent()) return;
          const data = readOrderData(response);
          setOrderResult({ orderId, userToken, userUid: uid, data });
        } catch (error) {
          if (!isCurrent()) return;
          const failure = getQueryError(error);
          if (failure.isLocked) {
            queryLockRef.current = true;
            setOrderResult({ orderId, userToken, userUid: uid, data: { requires_query_password: true, query_locked: true } });
            return;
          }
          setOrderResult(null);
          setQueryError(failure.message);
        }
      }
    };

    init().finally(() => {
      if (isCurrent()) setIsQuerying(false);
    });
    return () => {
      cancelled = true;
      requestVersion.current += 1;
    };
  }, [orderId, userToken, dispatch, user?.uid, refreshIndex]);

  const handleRefresh = () => {
    if (queryLockRef.current) return;
    requestVersion.current += 1;
    passwordRequestPending.current = false;
    setOrderResult(null);
    setQueryPassword('');
    setShowQueryPassword(false);
    setPasswordError('');
    setQueryError('');
    setIsVerifying(false);
    setIsQuerying(true);
    setRefreshIndex((index) => index + 1);
  };

  const handleVerifyPassword = async (event) => {
    event.preventDefault();
    if (passwordRequestPending.current || queryLockRef.current || isQueryLocked || isQuerying || !requiresQueryPassword) return;
    // 只检查是否为空；兼容旧密码，保留所有空格、前导零和纯数字密码。
    if (queryPassword === '') {
      setPasswordError('请输入购买此单时设置的查询密码。');
      passwordInputRef.current?.focus();
      return;
    }
    const uid = localStorage.getItem('uid') || user?.uid;
    if (!uid || !orderId) {
      setPasswordError(QUERY_ERROR_MESSAGES.INVALID_REQUEST);
      return;
    }
    const version = ++requestVersion.current;
    passwordRequestPending.current = true;
    setIsVerifying(true);
    setPasswordError('');
    setCopied(false);
    setCopiedKey(false);
    setCopiedField(null);
    const lockedResult = { orderId, userToken, userUid: user?.uid, data: { requires_query_password: true } };
    setOrderResult(lockedResult);
    try {
      const response = await getOrderProductInfoWithPassword({
        orderId: String(orderId), uid: String(uid), query_password: queryPassword
      });
      if (requestVersion.current !== version) return;
      const data = readOrderData(response);
      if (data.requires_query_password !== false || data.query_password_verified !== true) {
        throw new Error('Unverified order response');
      }
      // 完整替换本次详情，不保留之前返回的卡密或账号。
      setOrderResult({ orderId, userToken, userUid: user?.uid, data });
      setQueryPassword('');
      setShowQueryPassword(false);
    } catch (error) {
      if (requestVersion.current !== version) return;
      setOrderResult(lockedResult);
      const failure = getQueryError(error);
      if (failure.isLocked) {
        queryLockRef.current = true;
        setOrderResult({ ...lockedResult, data: { requires_query_password: true, query_locked: true } });
        setQueryPassword('');
        setShowQueryPassword(false);
        setPasswordError('');
        setOpenFaq(null);
        return;
      }
      if (failure.code === 'QUERY_PASSWORD_NOT_SET') {
        handleRefresh();
        return;
      }
      setPasswordError(failure.message);
      passwordInputRef.current?.focus();
    } finally {
      if (requestVersion.current === version) {
        passwordRequestPending.current = false;
        setIsVerifying(false);
      }
    }
  };

  const handleCopyKey = () => {
    if (!canSelfActivate) return;

    navigator.clipboard.writeText(productInfo.card_key).then(() => {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    });
  };

  const handleCopyField = (field, value) => {
    if (requiresQueryPassword || !productInfo || !value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleCopy = () => {
    const lines = [`订单号: ${orderId}`];
    if (productInfo?.out_trade_no) lines.push(`交易号: ${productInfo.out_trade_no}`);
    if (productInfo?.product_name) lines.push(`产品: ${productInfo.product_name}`);
    if (user?.uid) lines.push(`邮箱: ${user.uid}`);
    const text = lines.join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!orderId) {
    return <div className="page-shell max-w-3xl"><PageHeading Icon={Search} eyebrow="ORDER · 订单查询" title="找到订单，领取交付内容" description="从历史订单进入，即可查看对应产品的卡密、账号或网页入口。" /><div className="page-card text-center"><p className="text-sm text-muted mb-6">当前没有选择订单，请先打开历史订单。</p><button onClick={() => navigate('/orders')} className="brand-purchase">查看历史订单<ArrowRight size={18} /></button></div></div>;
  }

  if (isQuerying) {
    return (
      <div className="min-h-screen bg-canvas px-4 py-12 sm:py-20">
        <div role="status" aria-live="polite" aria-busy="true" className="mx-auto max-w-lg rounded-2xl border border-brand-line bg-white p-6 sm:p-10 text-center shadow-[0_20px_70px_rgba(234,88,12,0.08)]">
          <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-soft">
            <span aria-hidden="true" className="absolute inset-0 rounded-full border border-brand-line motion-safe:animate-pulse" />
            <Loader2 aria-hidden="true" className="h-9 w-9 text-brand motion-safe:animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-ink">正在查询订单</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted">正在获取您的订单信息与卡密领取状态，<br className="hidden sm:block" />请稍候，无需重复刷新。</p>
          <div aria-hidden="true" className="mt-8 space-y-3 rounded-2xl border border-line bg-canvas/70 p-5 text-left motion-safe:animate-pulse">
            <div className="h-3 w-20 rounded-full bg-gray-200" />
            <div className="h-4 w-3/4 rounded-full bg-gray-200/80" />
            <div className="pt-3 flex gap-3">
              <div className="h-10 flex-1 rounded-xl bg-gray-200/60" />
              <div className="h-10 flex-1 rounded-xl bg-gray-200/60" />
            </div>
          </div>
          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted">
            <ShieldCheck aria-hidden="true" className="h-4 w-4" />您的卡密和充值地址仅在符合查询条件时展示
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="container mx-auto px-4 py-8 max-w-2xl">

        {/* 查询结果标题 */}
        <div className="mb-6">
          <PageHeading Icon={requiresQueryPassword ? ShieldCheck : productInfo ? CheckCircle : Search} eyebrow="ORDER · 查询与交付" title={isQueryLocked ? '订单查询已锁定' : requiresQueryPassword ? '订单已找到，请验证查询密码' : productInfo ? '支付成功' : '订单查询'} description={orderDescription} />
          {!isQueryLocked && <button type="button" onClick={handleRefresh} className="mt-3 text-sm font-semibold text-brand hover:text-brand underline underline-offset-4">
            重新查询
          </button>}
        </div>

        {isQueryLocked && (
          <section aria-labelledby="query-locked-title" className="mb-6 overflow-hidden rounded-2xl border-2 border-red-200 bg-white shadow-sm">
            <div className="bg-red-50 p-5 sm:p-7">
              <LockKeyhole aria-hidden="true" className="h-8 w-8 text-red-600 mb-3" />
              <h2 id="query-locked-title" className="text-xl font-bold text-red-900">请联系客服解锁</h2>
              <p role="alert" className="mt-3 text-sm leading-relaxed text-red-800">{QUERY_ERROR_MESSAGES.ORDER_QUERY_LOCKED}</p>
              <p className="mt-2 text-xs leading-relaxed text-red-700">已停止密码提交。刷新页面或更改订单号格式不会解除锁定。</p>
            </div>
            <div className="p-5 sm:p-7">
              <p className="text-sm leading-relaxed text-muted">请向客服提供下方订单号，由客服核实并解锁，无需提供查询密码。客服确认解锁后，可刷新页面重新查询。</p>
              <a href="/customer-service" target="_blank" rel="noopener noreferrer" className="mt-5 flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-white hover:bg-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
                联系客服解锁<ArrowRight aria-hidden="true" className="h-4 w-4" />
              </a>
            </div>
          </section>
        )}

        {requiresQueryPassword && !isQueryLocked && (
          <section aria-labelledby="query-password-title" className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-[0_16px_50px_rgba(234,88,12,0.12)] mb-6">
            <div className="bg-brand-soft px-5 py-6 sm:px-7 sm:py-7">
              <div className="flex items-center gap-2 text-xs font-semibold text-brand mb-3">
                <ShieldCheck aria-hidden="true" className="h-4 w-4" />此订单需要查询密码
              </div>
              <h2 id="query-password-title" className="text-xl sm:text-2xl font-bold text-ink">输入查询密码，查看卡密</h2>
              <p id="query-password-help" className="mt-2 text-sm leading-relaxed text-muted">
                请使用购买此单时设置的密码，验证后即可查看卡密和充值地址。
              </p>
            </div>
            <form onSubmit={handleVerifyPassword} aria-busy={isVerifying} className="p-5 sm:p-7">
              <label htmlFor="query-password" className="block text-base font-bold text-ink mb-3">
                查询密码
              </label>
              <div className="relative rounded-2xl shadow-sm">
                <LockKeyhole aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand" />
                <input
                  ref={passwordInputRef}
                  id="query-password"
                  name="queryPassword"
                  type={showQueryPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={queryPassword}
                  readOnly={isVerifying}
                  onChange={(event) => {
                    setQueryPassword(event.target.value);
                    setPasswordError('');
                  }}
                  placeholder="输入此单查询密码"
                  aria-invalid={Boolean(passwordError)}
                  aria-describedby={`query-password-help query-password-note${passwordError ? ' query-password-error' : ''}`}
                  className="w-full min-h-[64px] pl-12 pr-16 py-4 border border-brand-line rounded-2xl bg-brand-soft/40 text-base text-ink placeholder:text-muted focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand focus:outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowQueryPassword((visible) => !visible)}
                  aria-label={showQueryPassword ? '隐藏查询密码' : '显示查询密码'}
                  aria-controls="query-password"
                  className="absolute inset-y-0 right-0 flex items-center justify-center min-w-[52px] text-muted hover:text-brand rounded-r-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                >
                  {showQueryPassword ? <EyeOff aria-hidden="true" className="h-5 w-5" /> : <Eye aria-hidden="true" className="h-5 w-5" />}
                </button>
              </div>
              {passwordError && (
                <p id="query-password-error" role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">
                  {passwordError}
                </p>
              )}
              <p id="query-password-note" className="mt-3 text-xs leading-relaxed text-muted">这是您下单时单独设置的查询密码，无需填写 ChatGPT 账号密码。</p>
              <p className="mt-2 text-xs leading-relaxed text-muted">密码输错 2 次将锁定订单查询，需联系客服解锁。请确认后再提交。</p>
              <button type="submit" disabled={isVerifying} className="mt-5 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-white shadow-md hover:bg-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-60 disabled:cursor-wait transition-colors">
                {isVerifying ? (
                  <><Loader2 aria-hidden="true" className="h-4 w-4 motion-safe:animate-spin" />正在验证查询密码…</>
                ) : (
                  <><LockKeyhole aria-hidden="true" className="h-4 w-4" />验证并查看卡密<ArrowRight aria-hidden="true" className="h-4 w-4" /></>
                )}
              </button>
              <p className="mt-2.5 text-center text-xs leading-relaxed text-muted">仅验证本次查询，重新查询时需再次输入密码。</p>
            </form>
          </section>
        )}

        {/* 常见问题 —— 仅有真实卡密的自助订单显示；人工升级订单无卡密，自助教程会误导用户，故隐藏 */}
        {canSelfActivate && (
        <div className="mb-4 rounded-2xl border border-brand-line overflow-hidden shadow-sm">
          {FAQ_ITEMS.map((item, index) => (
            <div key={item.id} className={index > 0 ? 'border-t border-brand-line' : ''}>
              <button
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-brand-soft hover:bg-brand-soft transition-colors"
                onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
              >
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">?</span>
                <span className="flex-1 text-sm font-bold text-ink">{item.question}</span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 text-brand transition-transform duration-200 ${openFaq === item.id ? 'rotate-180' : ''}`}
                />
              </button>
              {openFaq === item.id && (
                <div className="px-4 pt-3 pb-4 bg-white border-t border-brand-line">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {/* GPT 成品普号 */}
        {!requiresQueryPassword && productInfo?.account_name && (
          <div className="mb-4 rounded-2xl overflow-hidden border-2 border-accent-line shadow-panel">
            <div className="bg-accent px-5 py-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-white text-base">🤖</span>
                <h2 className="text-white font-bold text-base tracking-wide">GPT 成品普号</h2>
              </div>
              <p className="text-blue-100 text-xs leading-relaxed">
                {canSelfActivate
                  ? '下方是你的 GPT 普通账号，搭配卡密可自助升级为 Plus，按步骤操作即可。'
                  : '下方是你的 GPT 普通账号。本订单由客服人工升级，请将订单号发送给客服，为此账号开通 Plus。'}
              </p>
            </div>
            <div className="bg-white px-5 py-4 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold text-muted tracking-widest uppercase">账号 / Account</p>
                  <button
                    onClick={() => handleCopyField('account_name', productInfo.account_name)}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-accent-soft border border-accent-line text-accent hover:bg-accent-soft px-3 py-1.5 rounded-full transition-colors"
                  >
                    {copiedField === 'account_name' ? <><Check className="w-3.5 h-3.5" /><span>已复制</span></> : <><Copy className="w-3.5 h-3.5" /><span>复制</span></>}
                  </button>
                </div>
                <div className="bg-canvas border border-line rounded-2xl px-4 py-3">
                  <p className="font-mono text-base font-bold text-ink break-all">{productInfo.account_name}</p>
                </div>
              </div>
              {productInfo?.account_password && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold text-muted tracking-widest uppercase">账号密码 / Password</p>
                    <button
                      onClick={() => handleCopyField('account_password', productInfo.account_password)}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-accent-soft border border-accent-line text-accent hover:bg-accent-soft px-3 py-1.5 rounded-full transition-colors"
                    >
                      {copiedField === 'account_password' ? <><Check className="w-3.5 h-3.5" /><span>已复制</span></> : <><Copy className="w-3.5 h-3.5" /><span>复制</span></>}
                    </button>
                  </div>
                  <div className="bg-canvas border border-line rounded-2xl px-4 py-3">
                    <p className="font-mono text-base font-bold text-ink break-all">{productInfo.account_password}</p>
                  </div>
                </div>
              )}
              {productInfo?.email_password && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold text-muted tracking-widest uppercase">邮箱密码 / Email Password</p>
                    <button
                      onClick={() => handleCopyField('email_password', productInfo.email_password)}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-accent-soft border border-accent-line text-accent hover:bg-accent-soft px-3 py-1.5 rounded-full transition-colors"
                    >
                      {copiedField === 'email_password' ? <><Check className="w-3.5 h-3.5" /><span>已复制</span></> : <><Copy className="w-3.5 h-3.5" /><span>复制</span></>}
                    </button>
                  </div>
                  <div className="bg-canvas border border-line rounded-2xl px-4 py-3">
                    <p className="font-mono text-base font-bold text-ink break-all">{productInfo.email_password}</p>
                  </div>
                </div>
              )}
              <div className="bg-red-50 border-2 border-red-300 rounded-2xl px-4 py-3">
                <p className="text-sm font-bold text-red-700 mb-1">邮箱账户无法正常打开？</p>
                <p className="text-xs text-red-600 leading-relaxed">
                  可以尝试切换使用 Firefox 火狐浏览器或 Edge 浏览器打开。推荐优先使用 Firefox 火狐浏览器，基本不会出现打不开的问题。
                </p>
              </div>
              <div className="bg-accent-soft border border-accent-line rounded-2xl px-4 py-3 mt-1">
                <p className="text-xs text-accent leading-relaxed">
                  {canSelfActivate
                    ? '👇 使用下方卡密和激活地址，登录此账号后自助完成 Plus 升级即可。'
                    : '👇 请点击右下角在线客服气泡，发送订单号，由客服为此账号手动升级 Plus。'}
                </p>
              </div>
            </div>
          </div>
        )}

        {!requiresQueryPassword && productInfo?.accessToken && (
          <div className="mb-4 rounded-2xl border-2 border-accent-line bg-white p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-bold text-ink">账号 Token</h2>
              <button type="button" onClick={() => handleCopyField('accessToken', productInfo.accessToken)} className="rounded-full border border-accent-line bg-accent-soft px-3 py-1.5 text-xs font-semibold text-accent hover:bg-accent-soft">
                {copiedField === 'accessToken' ? '已复制' : '复制 Token'}
              </button>
            </div>
            <p className="max-h-40 overflow-y-auto break-all rounded-xl bg-canvas p-4 font-mono text-sm text-ink">{productInfo.accessToken}</p>
          </div>
        )}

        {/* 自助卡密 & 激活地址 */}
        {canShowActivation && (
          <div className="mb-4 rounded-2xl overflow-hidden border-2 border-sage-400 shadow-panel">
            {/* 模块头部 */}
            <div className="bg-sage-500 px-5 py-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-white text-base">⚡</span>
                <h2 className="text-white font-bold text-base tracking-wide">自助卡密 &amp; 激活地址</h2>
              </div>
              <p className="text-sage-100 text-xs leading-relaxed">
                按下方步骤可自助完成激活，无需等待客服。遇到任何问题请点击右下角客服气泡。
              </p>
            </div>

            <div className="bg-white px-5 py-4 space-y-4">
              {/* 卡密 */}
              {canSelfActivate && <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-sage-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center">1</span>
                    <span className="text-xs font-bold text-muted tracking-widest uppercase">🔑 激活卡密</span>
                  </div>
                  <button
                    onClick={handleCopyKey}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-sage-50 border border-sage-300 text-sage-700 hover:bg-sage-100 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {copiedKey ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>已复制</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>复制卡密</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="bg-sage-50 border-2 border-sage-300 rounded-2xl px-4 py-3 relative">
                  <p className="font-mono text-base font-bold text-ink break-all tracking-widest">
                    {productInfo.card_key}
                  </p>
                  <p className="text-[10px] text-sage-600 font-semibold mt-1.5">👆 先复制这串卡密，下一步要用！</p>
                </div>
              </div>}

              {canSelfActivate && productInfo.baseurl && <div className="border-t border-line" />}

              {/* 激活地址 */}
              {productInfo.baseurl && <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-sage-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center">2</span>
                  <span className="text-xs font-bold text-muted tracking-widest uppercase">🔗 激活地址</span>
                  <span className="ml-1 text-[10px] font-bold text-white bg-sage-500 px-1.5 py-0.5 rounded-full">点击打开</span>
                </div>
                <a
                  href={productInfo.baseurl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 bg-sage-50 border-2 border-sage-300 hover:border-sage-400 hover:bg-sage-100 rounded-2xl px-4 py-3 transition-colors group"
                >
                  <span className="font-mono text-sm font-semibold text-sage-700 group-hover:text-sage-800 break-all">
                    {productInfo.baseurl}
                  </span>
                  <span className="flex-shrink-0 text-sage-500 group-hover:text-sage-700 font-bold text-sm">↗</span>
                </a>
              </div>}
            </div>
          </div>
        )}

        {/* 未登录提示 */}
        {notLoggedIn && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5 mb-4 text-center">
            <p className="text-sm font-semibold text-red-600">当前未登录，无法查询订单信息</p>
            <p className="text-xs text-red-400 mt-1">请登录后刷新页面，或联系客服核实订单</p>
          </div>
        )}

        {queryError && (
          <div role="alert" className="bg-brand-soft border border-brand-line rounded-2xl p-5 mb-4">
            <p className="text-sm font-bold text-brand">{queryError}</p>
            <p className="text-xs text-muted leading-relaxed mt-2">请确认付款时使用的账户及订单号，或联系客服核实。</p>
          </div>
        )}

        {/* 订单信息卡 - 突出 */}
        <div className="bg-brand-soft border border-brand-line rounded-2xl p-5 mb-4">
          <div className="flex items-start justify-between mb-4">
            <p className="text-xs font-bold text-muted tracking-widest">YOUR ORDER</p>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs font-semibold bg-white border border-brand-line text-muted hover:bg-brand-soft px-3 py-1.5 rounded-full transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>已复制</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>复制订单信息</span>
                </>
              )}
            </button>
          </div>

          {/* 系统订单号（短）—— 用户付款后会用到 */}
          <div className="mb-3">
            <p className="text-xs text-muted mb-1">系统订单号</p>
            <p className="font-mono text-base font-bold text-ink break-all">
              {orderId || <span className="text-red-500 text-sm">[URL 中缺少 orderId 参数]</span>}
            </p>
          </div>

          {/* 支付交易号（长）—— 客服查单/对账要用 */}
          {productInfo?.out_trade_no && (
            <div className="mb-3">
              <p className="text-xs text-muted mb-1">支付交易号</p>
              <p className="font-mono text-sm font-semibold text-ink break-all">{productInfo.out_trade_no}</p>
            </div>
          )}

          <div className="space-y-2 pt-3 border-t border-brand-line">
            {productInfo?.product_name && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 text-sm">
                <span className="text-muted">产品名称</span>
                <span className="min-w-0 break-words font-semibold text-ink sm:text-right">{productInfo.product_name}</span>
              </div>
            )}
            {user?.uid && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 text-sm">
                <span className="text-muted">账号邮箱</span>
                <span className="min-w-0 font-semibold text-ink sm:text-right break-all">{user.uid}</span>
              </div>
            )}
          </div>
        </div>

        {/* 下一步 */}
        {isManualUpgrade ? (
          <div className="bg-white border border-line rounded-2xl p-5 shadow-panel mb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold text-muted tracking-widest">NEXT STEP</span>
            </div>
            <h2 className="text-lg font-bold text-ink mb-3">联系客服开通</h2>
            <div className="flex items-center gap-2 bg-brand-soft border border-brand-line rounded-xl px-3 py-2 mb-3">
              <span className="text-base flex-shrink-0">🕘</span>
              <span className="text-xs font-bold text-brand">客服在线时间：每天 9:00 — 23:00</span>
            </div>
            <div className="space-y-2.5">
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-5 h-5 bg-brand text-white text-[11px] font-bold rounded-full flex items-center justify-center">1</span>
                <p className="text-sm text-ink leading-relaxed">复制上方订单号，点击右下角<span className="font-semibold text-ink">在线客服气泡</span>，发给客服</p>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-5 h-5 bg-brand text-white text-[11px] font-bold rounded-full flex items-center justify-center">2</span>
                <p className="text-sm text-ink leading-relaxed">向客服提交你的<span className="font-semibold text-ink">账号信息</span>，由客服为你<span className="font-semibold text-ink">手动开通升级</span></p>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-5 h-5 bg-brand text-white text-[11px] font-bold rounded-full flex items-center justify-center">3</span>
                <p className="text-sm text-ink leading-relaxed">开通完成后客服会通知你，<span className="font-semibold text-ink">全程人工操作</span>，安全省心</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-canvas border border-line rounded-2xl px-5 py-4 mb-4">
            <p className="text-sm text-muted leading-relaxed">
              遇到任何问题，点击右下角<span className="font-semibold text-ink">在线客服气泡</span>随时联系我们。
            </p>
          </div>
        )}

        {/* 联系方式 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {/* 在线客服 */}
          <div className="border border-line rounded-2xl p-4 bg-white relative overflow-hidden">

            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">💬</span>
              <span className="font-bold text-ink text-sm">在线客服</span>
            </div>
            <p className="text-xs text-muted mb-2 leading-relaxed">点击右下角气泡即时沟通</p>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-sage-500 rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold text-ink">9:00 — 23:00</span>
            </div>
          </div>

          {/* 邮件 */}
          <div className="border border-line rounded-2xl p-4 bg-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">✉️</span>
              <span className="font-bold text-ink text-sm">邮件联系</span>
            </div>
            <a
              href="mailto:ricky@savvyai.plus"
              className="block text-sm font-mono font-semibold text-ink mb-1 hover:text-brand transition-colors break-all"
            >
              ricky@savvyai.plus
            </a>
            <p className="text-xs text-muted leading-relaxed">回复较慢，2–3 天</p>
          </div>
        </div>

        {/* 返回首页 */}
        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 border border-line text-ink text-sm font-semibold rounded-xl hover:bg-canvas transition-all duration-300"
          >
            返回首页
          </button>
        </div>

      </div>
    </div>
  );
}

export default PaymentSuccess;
